import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Package, AlertTriangle, Edit, Loader2 } from 'lucide-react';
import { formatBusinessDate } from '../lib/dateTime';

const ProductsPage = () => {
  const { api, isOwner, permissions } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    cost_price: '',
    selling_price: '',
    tax_classification: 'vatable',
    stock_quantity: '',
    low_stock_threshold: '10',
    unit: 'piece',
    barcode: '',
    track_expiry: false,
  });

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        api.get('/products', { params: { include_inactive: showInactive } }),
        api.get('/products/categories'),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      const lowStockRes = await api.get('/products/low-stock').catch(() => ({ data: [] }));
      setLowStockProducts(lowStockRes.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [api, showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      cost_price: '',
      selling_price: '',
      tax_classification: 'vatable',
      stock_quantity: '',
      low_stock_threshold: '10',
      unit: 'piece',
      barcode: '',
      track_expiry: false,
    });
    setEditingProduct(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      cost_price: product.cost_price.toString(),
      selling_price: product.selling_price.toString(),
      tax_classification: product.tax_classification,
      stock_quantity: product.stock_quantity.toString(),
      low_stock_threshold: product.low_stock_threshold.toString(),
      unit: product.unit,
      barcode: product.barcode || '',
      track_expiry: Boolean(product.track_expiry),
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.category || !formData.selling_price) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        cost_price: parseFloat(formData.cost_price) || 0,
        selling_price: parseFloat(formData.selling_price),
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        low_stock_threshold: parseInt(formData.low_stock_threshold) || 10,
        track_expiry: Boolean(formData.track_expiry),
      };

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, payload);
        toast.success('Product updated successfully');
      } else {
        await api.post('/products', payload);
        toast.success('Product added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await api.delete(`/products/${productId}`);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleRestore = async (productId) => {
    try {
      await api.post(`/products/${productId}/restore`);
      toast.success('Product restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore product');
    }
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesLowStock = !lowStockOnly || p.stock_quantity <= p.low_stock_threshold;
    return matchesSearch && matchesCategory && matchesLowStock;
  });

  const formatCurrency = (amount) => {
    return `UGX ${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const canManageProducts = isOwner || permissions?.manage_products !== false;
  const canDelete = isOwner || permissions?.delete_products === true;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Products</h1>
          <p className="text-muted-foreground">{products.length} products in inventory</p>
        </div>
        {canManageProducts && (
          <Button onClick={openAddDialog} className="neon-green" data-testid="add-product-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Product
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search by name or SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
            data-testid="products-search"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-48 h-12">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={showInactive ? 'default' : 'outline'}
          onClick={() => setShowInactive((v) => !v)}
          className="h-12"
        >
          {showInactive ? 'Hiding Archived' : 'Show Archived'}
        </Button>
        <Button
          type="button"
          variant={lowStockOnly ? 'default' : 'outline'}
          onClick={() => setLowStockOnly((v) => !v)}
          className="h-12"
        >
          Low Stock ({lowStockProducts.length})
        </Button>
      </div>

      {/* Products Table */}
      <Card className="shadow-sm border-border/60 bg-card/95">
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-300px)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tax</TableHead>
                  {canManageProducts && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-xs text-muted-foreground">{product.sku}</p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {product.track_expiry ? (
                            <Badge variant="secondary">Expiry tracked</Badge>
                          ) : null}
                          {product.nearest_expiry_date ? (
                            <Badge variant="outline">
                              Next expiry {formatBusinessDate(product.nearest_expiry_date)}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{product.category}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatCurrency(product.cost_price)}
                    </TableCell>
                    <TableCell className="font-mono text-sm font-semibold text-primary">
                      {formatCurrency(product.selling_price)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {product.stock_quantity <= product.low_stock_threshold && (
                          <AlertTriangle className="h-4 w-4 text-warning" />
                        )}
                        <span className={product.stock_quantity <= product.low_stock_threshold ? 'text-warning' : ''}>
                          {product.stock_quantity} {product.unit}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.is_active ? 'default' : 'secondary'}>
                        {product.is_active ? 'Active' : 'Archived'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={product.tax_classification === 'vatable' ? 'default' : 'secondary'}>
                        {product.tax_classification === 'vatable' ? 'VAT' : 
                         product.tax_classification === 'zero_rated' ? 'Zero' : 'Exempt'}
                      </Badge>
                    </TableCell>
                    {canManageProducts && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {product.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(product)}
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && product.is_active && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(product.id)}
                            >
                              Delete
                            </Button>
                          )}
                          {canDelete && !product.is_active && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestore(product.id)}
                            >
                              Restore
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {filteredProducts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canManageProducts ? 8 : 7} className="text-center py-12">
                      <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No products found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Product Name *</Label>
                <Input
                  placeholder="e.g., Sugar 1kg"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  data-testid="product-name-input"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="Optional product description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  data-testid="product-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Category *</Label>
                <Input
                  placeholder="e.g., Groceries"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  list="categories"
                  data-testid="product-category-input"
                />
                <datalist id="categories">
                  {categories.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => handleChange('unit', v)}>
                  <SelectTrigger data-testid="product-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="piece">Piece</SelectItem>
                    <SelectItem value="kg">Kilogram</SelectItem>
                    <SelectItem value="litre">Litre</SelectItem>
                    <SelectItem value="pack">Pack</SelectItem>
                    <SelectItem value="carton">Carton</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Cost Price (UGX)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.cost_price}
                  onChange={(e) => handleChange('cost_price', e.target.value)}
                  className="font-mono"
                  data-testid="product-cost-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Selling Price (UGX) *</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.selling_price}
                  onChange={(e) => handleChange('selling_price', e.target.value)}
                  className="font-mono"
                  data-testid="product-price-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Stock Quantity</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={formData.stock_quantity}
                  onChange={(e) => handleChange('stock_quantity', e.target.value)}
                  data-testid="product-stock-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Low Stock Alert</Label>
                <Input
                  type="number"
                  placeholder="10"
                  value={formData.low_stock_threshold}
                  onChange={(e) => handleChange('low_stock_threshold', e.target.value)}
                  data-testid="product-threshold-input"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Tax Classification</Label>
                <Select value={formData.tax_classification} onValueChange={(v) => handleChange('tax_classification', v)}>
                  <SelectTrigger data-testid="product-tax-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vatable">VATable (18%)</SelectItem>
                    <SelectItem value="zero_rated">Zero Rated (0%)</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  VATable: Most goods • Zero-rated: Exports, airtime • Exempt: Basic food, medical
                </p>
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Barcode (Optional)</Label>
                <Input
                  placeholder="Scan or enter barcode"
                  value={formData.barcode}
                  onChange={(e) => handleChange('barcode', e.target.value)}
                  data-testid="product-barcode-input"
                />
              </div>

              <div className="col-span-2 rounded-lg border border-border/60 bg-background/70 p-4 space-y-3 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="track-expiry" className="text-sm font-medium">
                      Track expiry by stock batch
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Turn this on for medicines and other products that expire. Expiry date will be captured when receiving stock.
                    </p>
                  </div>
                  <Checkbox
                    id="track-expiry"
                    checked={formData.track_expiry}
                    onCheckedChange={(checked) => handleChange('track_expiry', checked === true)}
                    data-testid="product-track-expiry-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} className="neon-green" data-testid="save-product-btn">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingProduct ? 'Update Product' : 'Add Product'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
